import { useEffect, useState } from "react";
import { ArrowLeft, FileText, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface GeneralDocument {
  id: string;
  name: string;
  path: string;
  el_doc_id?: string;
  isCategory?: boolean;
}

const GeneralDataLibrary = () => {
  const [categories, setCategories] = useState<string[]>([]);
  const [documents, setDocuments] = useState<GeneralDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<GeneralDocument | null>(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentContent, setDocumentContent] = useState<string>("");
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<string | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<string>("Categories");

  useEffect(() => {
    fetchTopLevelCategories();
  }, []);

  const fetchTopLevelCategories = async () => {
    setIsLoading(true);
    setCurrentCategory(null);
    setBreadcrumb("Categories");

    try {
      const response = await fetch("https://app.enterprisedna.co/api/v1/pdf/categories", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) throw new Error("Failed to fetch categories");

      const data = await response.json();
      setCategories(data);

      const docsFromCategories = data.map((category: string) => {
        const categoryName = category.split("/").pop() || category;
        const formattedName = categoryName.charAt(0).toUpperCase() + categoryName.slice(1);

        return {
          id: category,
          name: formattedName,
          path: category,
          isCategory: true,
        };
      });

      setDocuments(docsFromCategories);
    } catch (error) {
      console.error("Error fetching top-level categories:", error);
      toast.error("Failed to load document categories");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategoryDocuments = async (categoryPath: string) => {
    setIsLoading(true);

    const categoryName = categoryPath.split("/").pop() || "";
    console.log(categoryName, "Fetching documents for category");

    try {
      const response = await fetch(`https://app.enterprisedna.co/api/v1/pdf/categories?category=${categoryName}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) throw new Error(`Failed to fetch documents for ${categoryName}`);

      const data = await response.json();
      console.log(`Fetched documents for ${categoryName}:`, data);

      const documentsInCategory = data.map((docPath: string) => {
        const fileName = docPath.split("/").pop() || docPath;
        const nameWithoutExtension = fileName.replace(".txt", "");
        const formattedName = nameWithoutExtension.charAt(0).toUpperCase() + nameWithoutExtension.slice(1);

        return {
          id: docPath,
          name: formattedName,
          path: docPath,
          isCategory: false,
        };
      });

      setDocuments(documentsInCategory);
      setCurrentCategory(categoryPath);

      // Update breadcrumb
      const formattedCategoryName = categoryName.charAt(0).toUpperCase() + categoryName.slice(1);
      setBreadcrumb(formattedCategoryName);
    } catch (error) {
      console.error(`Error fetching documents for ${categoryName}:`, error);
      toast.error(`Failed to load documents for this category`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDocumentContent = async (docPath: string) => {
    setIsContentLoading(true);
    setDocumentContent("");

    try {
      const url = `https://s3.ap-southeast-2.amazonaws.com/fs.enterprisedna.co/${docPath}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch document content`);

      const content = await response.text();
      setDocumentContent(content);
      return content;
    } catch (error) {
      console.error(`Error fetching document content:`, error);
      toast.error(`Failed to load document content`);
      return "";
    } finally {
      setIsContentLoading(false);
    }
  };

  const handleItemClick = async (item: GeneralDocument) => {
    if (item.isCategory) {
      fetchCategoryDocuments(item.path);
    } else {
      openDocumentDetails(item);
    }
  };

  const openDocumentDetails = async (doc: GeneralDocument) => {
    setSelectedDocument(doc);
    await fetchDocumentContent(doc.path);
    setShowDocumentModal(true);
  };

  const closeDocumentDetails = () => {
    setShowDocumentModal(false);
    setSelectedDocument(null);
    setDocumentContent("");
  };

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <Dialog open={showDocumentModal} onOpenChange={(open) => !open && closeDocumentDetails()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{selectedDocument?.name}</DialogTitle>
            <DialogDescription>Document</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-[calc(100vh-320px)]">
              <div className="rounded-lg border border-[#E5EAF0] bg-white p-4">
                {isContentLoading ? (
                  <div className="flex justify-center items-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap">{documentContent}</div>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
      <div className="container mx-auto px-4 py-8 max-w-[1920px]">
        <div className="w-full">
          <PageHeader title="General Document Library" icon={FileText} />
          <div className="bg-white shadow rounded-lg p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                {currentCategory && (
                  <Button variant="ghost" size="sm" className="mr-2" onClick={fetchTopLevelCategories}>
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                )}
                <h2 className="text-xl font-semibold">{breadcrumb}</h2>
              </div>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map((item) => (
                  <div
                    key={item.id}
                    className="bg-gray-50 hover:bg-gray-100 p-4 rounded-md cursor-pointer transition duration-200"
                    onClick={() => handleItemClick(item)}
                  >
                    <div className="flex items-center">
                      {item.isCategory ? (
                        <FolderOpen className="h-5 w-5 mr-2 text-amber-500" />
                      ) : (
                        <FileText className="h-5 w-5 mr-2 text-blue-600" />
                      )}
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-gray-500">{item.isCategory ? "Category" : "Document"}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralDataLibrary;
